const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const initBanners = () => {
  if (reduceMotion || typeof window.gsap === "undefined") return;

  document.querySelectorAll("[data-nkh-banner]").forEach((banner) => {
    const slides = [...banner.querySelectorAll("[data-nkh-slide]")];
    if (slides.length < 2) return;

    const interval = (Number(banner.dataset.interval) || 6) * 1000;
    slides.forEach((slide, index) => {
      window.gsap.set(slide, { autoAlpha: index === 0 ? 1 : 0, xPercent: 0 });
    });

    let current = 0;
    window.setInterval(() => {
      const outgoing = slides[current];
      current = (current + 1) % slides.length;
      const incoming = slides[current];

      window.gsap.to(outgoing, {
        autoAlpha: 0,
        xPercent: -4,
        duration: 1.6,
        ease: "power2.inOut",
      });
      window.gsap.fromTo(
        incoming,
        { autoAlpha: 0, xPercent: 4 },
        { autoAlpha: 1, xPercent: 0, duration: 1.6, ease: "power2.inOut" }
      );
    }, interval);
  });
};

const canAnimate = () => !reduceMotion && typeof window.gsap !== "undefined";

const initCardHover = () => {
  if (!canAnimate() || !window.matchMedia("(hover: hover)").matches) return;

  document.querySelectorAll(".nkh-card").forEach((card) => {
    const lift = window.gsap.quickTo(card, "y", { duration: 0.8, ease: "power3.out" });
    card.addEventListener("pointerenter", () => lift(-6));
    card.addEventListener("pointerleave", () => lift(0));
  });
};

const cubicPoint = (progress, start, control1, control2, end) => {
  const remaining = 1 - progress;
  return {
    x:
      remaining ** 3 * start.x +
      3 * remaining ** 2 * progress * control1.x +
      3 * remaining * progress ** 2 * control2.x +
      progress ** 3 * end.x,
    y:
      remaining ** 3 * start.y +
      3 * remaining ** 2 * progress * control1.y +
      3 * remaining * progress ** 2 * control2.y +
      progress ** 3 * end.y,
  };
};

const flyProductToCart = (button) => {
  if (reduceMotion) return;

  const card = button.closest(".nkh-card");
  const image = card?.querySelector(".nkh-card__media img");
  const cart =
    document.querySelector(".header-actions__cart-icon") ||
    document.querySelector("cart-icon") ||
    document.querySelector("[data-testid='cart-icon']");
  const imageUrl = image?.currentSrc || image?.getAttribute("src") || "";
  if (!card || !cart) return;

  const sourceRect = button.getBoundingClientRect();
  const cartRect = cart.getBoundingClientRect();
  const size = 52;
  const start = {
    x: sourceRect.left + sourceRect.width / 2 - size / 2,
    y: sourceRect.top + sourceRect.height / 2 - size / 2,
  };
  const end = {
    x: cartRect.left + cartRect.width / 2 - size / 2,
    y: cartRect.top + cartRect.height / 2 - size / 2,
  };
  const direction = Math.sign(end.x - start.x) || 1;
  const distance = Math.abs(end.x - start.x);
  const control1 = {
    x: start.x + direction * Math.min(120, distance * 0.22),
    y: start.y - Math.min(110, Math.max(60, window.innerHeight * 0.1)),
  };
  const control2 = {
    x: end.x - direction * Math.min(150, distance * 0.28),
    y: end.y + 54,
  };

  const flyer = document.createElement("span");
  flyer.className = "nkh-fly-to-cart";
  flyer.setAttribute("aria-hidden", "true");
  if (imageUrl) flyer.style.backgroundImage = `url("${imageUrl.replaceAll('"', '\\"')}")`;
  flyer.style.transform = `translate3d(${start.x}px, ${start.y}px, 0) scale(1)`;
  flyer.style.opacity = "1";
  document.body.appendChild(flyer);

  const duration = 1050;
  const startTime = performance.now();
  const tick = (time) => {
    const rawProgress = Math.min((time - startTime) / duration, 1);
    const progress = 1 - (1 - rawProgress) ** 3;
    const point = cubicPoint(progress, start, control1, control2, end);
    const scale = 1 - progress * 0.62;
    const opacity = rawProgress > 0.82 ? (1 - rawProgress) / 0.18 : 1;

    flyer.style.transform = `translate3d(${point.x}px, ${point.y}px, 0) scale(${scale})`;
    flyer.style.opacity = String(Math.max(0, opacity));

    if (rawProgress < 1) {
      requestAnimationFrame(tick);
    } else {
      flyer.remove();
    }
  };

  requestAnimationFrame(tick);
};

const initQuickAddInteractions = () => {
  document.querySelectorAll(".nkh-releases .quick-add").forEach((wrap) => {
    let closeTimer;

    wrap.addEventListener("pointerenter", () => {
      window.clearTimeout(closeTimer);
      wrap.classList.add("is-expanded");
    });

    wrap.addEventListener("pointerleave", () => {
      closeTimer = window.setTimeout(() => {
        wrap.classList.remove("is-expanded");
      }, 220);
    });
  });

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest(".nkh-releases .quick-add__button");
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;

      button.closest(".quick-add")?.classList.remove("is-expanded");
      flyProductToCart(button);
    },
    true
  );
};

const initNavEntrance = () => {
  const links = document.querySelectorAll(".nkh-nav__link");
  if (!links.length) return;

  window.gsap.fromTo(
    links,
    { x: 18, autoAlpha: 0 },
    {
      x: 0,
      autoAlpha: 1,
      duration: 1.2,
      ease: "power3.out",
      stagger: 0.09,
      delay: 0.15,
      clearProps: "transform",
    }
  );
};

const initCardEntrance = () => {
  const grids = document.querySelectorAll(".nkh-releases__grid");
  if (!grids.length) return;

  const reveal = (grid) => {
    window.gsap.fromTo(
      grid.querySelectorAll(".nkh-card"),
      { y: 32, autoAlpha: 0 },
      {
        y: 0,
        autoAlpha: 1,
        duration: 1.1,
        ease: "power3.out",
        stagger: (index) => Math.min(index, 6) * 0.08,
      }
    );
  };

  if (!("IntersectionObserver" in window)) {
    grids.forEach(reveal);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        reveal(entry.target);
      });
    },
    { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
  );

  grids.forEach((grid) => observer.observe(grid));
};

const initCarousels = () => {
  document.querySelectorAll("[data-nkh-carousel]").forEach((carousel) => {
    const track = carousel.querySelector("[data-nkh-carousel-track]");
    const nav = carousel.querySelector("[data-nkh-carousel-nav]");
    const prev = carousel.querySelector("[data-nkh-carousel-prev]");
    const next = carousel.querySelector("[data-nkh-carousel-next]");
    if (!track || !nav || !prev || !next) return;

    const fit = () => {
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const targetCard = track.clientWidth < 700 ? 160 : 280;
      const columns = Math.max(1, Math.round((track.clientWidth + gap) / (targetCard + gap)));
      track.style.setProperty("--nkh-cols", columns);
    };

    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      nav.hidden = maxScroll <= 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= maxScroll - 2;
    };

    const step = () => {
      const card = track.querySelector(".nkh-card");
      if (!card) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const cardSpan = card.getBoundingClientRect().width + gap;
      return Math.max(1, Math.floor((track.clientWidth + gap) / cardSpan)) * cardSpan;
    };

    const scroll = (direction) => {
      track.scrollBy({ left: direction * step(), behavior: reduceMotion ? "auto" : "smooth" });
    };

    prev.addEventListener("click", () => scroll(-1));
    next.addEventListener("click", () => scroll(1));
    track.addEventListener("scroll", update, { passive: true });

    const refresh = () => {
      fit();
      update();
    };

    if ("ResizeObserver" in window) {
      new ResizeObserver(refresh).observe(track);
    } else {
      window.addEventListener("resize", refresh);
    }

    refresh();
  });
};

const start = () => {
  const root = document.documentElement;
  initCarousels();
  initQuickAddInteractions();

  if (canAnimate()) {
    window.gsap.set(".nkh-card, .nkh-nav__link", { autoAlpha: 0 });
    root.classList.remove("nkh-motion-pending");
    initNavEntrance();
    initCardEntrance();
  } else {
    root.classList.remove("nkh-motion-pending");
  }

  initBanners();
  initCardHover();
  initBandCursor();
};

const initBandCursor = () => {
  if (!canAnimate() || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

  const photos = document.querySelectorAll(".nkh-band__media");
  if (!photos.length) return;

  const cursor = document.createElement("span");
  cursor.className = "nkh-band-cursor";
  cursor.textContent = "Zobacz więcej";
  cursor.setAttribute("aria-hidden", "true");
  document.body.appendChild(cursor);
  document.documentElement.classList.add("nkh-band-cursor-on");

  window.gsap.set(cursor, { xPercent: -50, yPercent: -50, autoAlpha: 0, scale: 0.9 });
  const moveX = window.gsap.quickTo(cursor, "x", { duration: 0.45, ease: "power3.out" });
  const moveY = window.gsap.quickTo(cursor, "y", { duration: 0.45, ease: "power3.out" });

  const place = (event) => {
    moveX(event.clientX);
    moveY(event.clientY);
  };

  photos.forEach((photo) => {
    photo.addEventListener("pointerenter", (event) => {
      window.gsap.set(cursor, { x: event.clientX, y: event.clientY });
      place(event);
      window.gsap.to(cursor, {
        autoAlpha: 1,
        scale: 1,
        duration: 0.45,
        ease: "power3.out",
        overwrite: "auto",
      });
    });
    photo.addEventListener("pointermove", place);
    photo.addEventListener("pointerleave", () => {
      window.gsap.to(cursor, {
        autoAlpha: 0,
        scale: 0.9,
        duration: 0.35,
        ease: "power3.out",
        overwrite: "auto",
      });
    });
  });

  document.addEventListener("click", (event) => {
    if (!event.target.closest("[data-nkh-band-open]")) return;
    window.gsap.to(cursor, {
      autoAlpha: 0,
      scale: 0.9,
      duration: 0.2,
      ease: "power3.out",
      overwrite: "auto",
    });
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
